import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { OnboardingDialog } from "@/components/OnboardingDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { playAlert, playExpressAlert, playNotify, getNotifPrefs, loadNotifPrefsFromServer, setupAudioUnlock, requestNotifyPermission, showNotification, canNotify, notifPermission } from "@/lib/notify";
import { subscribeWebPush } from "@/lib/webpush";
import { ThemePicker } from "@/components/ThemePicker";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { InstallAppButton } from "@/components/InstallAppButton";
import { useI18n } from "@/i18n";
import { BrandMark } from "@/components/BrandMark";
import { SessionStatusDot } from "@/components/SessionStatusDot";
import { AuroraBackground } from "@/components/AuroraBackground";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, UploadCloud, ListChecks, Coins, Users, ShieldCheck,
  LogOut, Cpu, FileStack, Gauge, Library, ScrollText, Settings, Bell, BellRing, LifeBuoy,
  ChevronDown, Wallet, CreditCard, SlidersHorizontal, Menu, X, Crown, ShieldAlert, Gift, AlertTriangle,
} from "lucide-react";

const clientNav = [
  { to: "/client", icon: LayoutDashboard, tk: "dashboard", label: "Tableau de bord", end: true },
  { to: "/client/new", icon: UploadCloud, tk: "newfile", label: "Nouveau fichier" },
  { to: "/client/orders", icon: ListChecks, tk: "orders", label: "Mes commandes" },
  { to: "/client/tickets", icon: LifeBuoy, tk: "tickets", label: "Tickets" },
  { to: "/client/credits", icon: Coins, tk: "credits", label: "Crédits" },
  { to: "/client/subscriptions", icon: Crown, tk: "subscriptions", label: "Abonnements" },
  { to: "/client/settings", icon: Settings, label: "Paramètres" },
];

const adminNav = [
  { to: "/admin", icon: Gauge, label: "Tableau de bord", end: true },
  { to: "/admin/orders", icon: FileStack, label: "File de traitement", perm: "orders" },
  { to: "/admin/defects", icon: AlertTriangle, label: "Journal des défauts", perm: "orders" },
  { to: "/admin/tickets", icon: LifeBuoy, label: "Tickets", perm: "tickets" },
  { to: "/admin/solutions", icon: Library, label: "Bibliothèque", perm: "solutions" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit", perm: "audit" },
  { to: "/admin/users", icon: Users, label: "Clients", perm: "users" },
  { to: "/admin/promo", icon: Gift, label: "Codes Promo", perm: "payments" },
  {
    label: "Paramètres", icon: Settings, perm: ["payments", "staff"],
    children: [
      { to: "/admin/settings/payments", icon: Wallet, label: "Paiements", perm: "payments" },
      { to: "/admin/settings/catalog", icon: CreditCard, label: "Catalogue & Prix", perm: "payments" },
      { to: "/admin/settings/notifications", icon: Bell, label: "Notifications" },
      { to: "/admin/settings/staff", icon: ShieldCheck, label: "Staff & Permissions", perm: "staff" },
    ],
  },
];

const ADMIN_LABELS = {
  "/admin": "Tableau de bord",
  "/admin/orders": "File de traitement",
  "/admin/defects": "Journal des défauts",
  "/admin/tickets": "Tickets",
  "/admin/solutions": "Bibliothèque",
  "/admin/audit": "Audit",
  "/admin/users": "Clients",
  "/admin/promo": "Codes Promo",
  "/admin/settings/payments": "Paiements",
  "/admin/settings/catalog": "Catalogue & Prix",
  "/admin/settings/notifications": "Notifications",
  "/admin/settings/services": "Catalogue & Prix",
  "/admin/settings/staff": "Staff & Permissions",
};

export const AppShell = ({ children, mode = "client" }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const nav = mode === "admin" ? adminNav : clientNav;
  const navLabel = (item) => (mode === "client" && item.tk ? t(`appnav.${item.tk}`) : item.label);
  const hasPerm = (perm) => {
    if (!perm) return true;
    if (user?.is_owner) return true;
    const p = user?.permissions || {};
    return Array.isArray(perm) ? perm.some((k) => p[k]) : !!p[perm];
  };
  const canSee = (item) => {
    if (mode !== "admin") return true;
    return hasPerm(item.perm);
  };
  const visibleNav = nav
    .filter(canSee)
    .map((item) => item.children ? { ...item, children: item.children.filter((c) => hasPerm(c.perm)) } : item)
    .filter((item) => !item.children || item.children.length > 0);
  const [openGroup, setOpenGroup] = useState(
    location.pathname.startsWith("/admin/settings") ? "Paramètres" : null
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);
  const [unread, setUnread] = useState(0);
  const [newOrders, setNewOrders] = useState(0);
  const [expressTickets, setExpressTickets] = useState(0);
  const [fraudAlerts, setFraudAlerts] = useState(0);
  const [pendingVehicles, setPendingVehicles] = useState(0);
  const [defectReports, setDefectReports] = useState(0);
  const [readyFiles, setReadyFiles] = useState(null);
  const badgeTarget = mode === "admin" ? "/admin/orders" : "/client/orders";
  const unreadRef = useRef(0);
  const prevUnread = useRef(0);
  const newOrdersRef = useRef(0);
  const prevNewOrders = useRef(0);
  const expressRef = useRef(0);
  const prevExpress = useRef(0);
  const prevFraud = useRef(0);
  const prevDefect = useRef(0);
  const baseTitle = useRef(typeof document !== "undefined" ? document.title : "Imperium");
  const [notifPerm, setNotifPerm] = useState(notifPermission());

  // Unlock Web Audio on the first user gesture so chimes/alerts can play afterwards.
  useEffect(() => { setupAudioUnlock(); }, []);
  useEffect(() => { loadNotifPrefsFromServer().catch(() => {}); }, []);
  // Refresh the Web Push subscription on load when notifications are already granted.
  useEffect(() => { if (notifPermission() === "granted") subscribeWebPush().catch(() => {}); }, []);

  const enableNotif = async () => {
    const res = await requestNotifyPermission();
    setNotifPerm(res);
    if (res === "granted") {
      toast.success("Notifications activées");
      showNotification("IMPERIUM", "Notifications activées — vous serez alerté des nouveaux messages.");
      subscribeWebPush().catch(() => {});
    } else if (res === "denied") {
      toast.error("Notifications bloquées par le navigateur (à réactiver dans les réglages du site).");
    }
  };

  useEffect(() => {
    let active = true;
    const tick = () => api.get("/notifications/unread")
      .then(({ data }) => { if (active) { setUnread(data.total || 0); setNewOrders(data.new_orders || 0); setExpressTickets(data.express_tickets || 0); setFraudAlerts(data.fraud_alerts || 0); setPendingVehicles(data.pending_vehicles || 0); setDefectReports(data.defect_reports || 0); setReadyFiles(data.ready_files ?? null); } })
      .catch(() => {});
    tick();
    const t = setInterval(tick, 15000);
    const onRefresh = () => tick();
    window.addEventListener("notif-refresh", onRefresh);
    return () => { active = false; clearInterval(t); window.removeEventListener("notif-refresh", onRefresh); };
  }, [location.pathname]);

  const notifyNew = (count) => {
    const body = mode === "admin"
      ? `Nouveau message client (${count} non lu${count > 1 ? "s" : ""})`
      : `Votre ingénieur vous a répondu (${count} non lu${count > 1 ? "s" : ""})`;
    showNotification("IMPERIUM · Nouveau message", body, () => navigate(badgeTarget));
  };

  const notifyNewOrder = (count) => {
    showNotification(
      "IMPERIUM · Nouvelle demande",
      `${count} commande${count > 1 ? "s" : ""} en attente de prise en charge`,
      () => navigate("/admin/orders")
    );
  };

  const notifyExpress = (count) => {
    showNotification(
      "🚨 IMPERIUM · Ticket EXPRESS",
      `${count} ticket${count > 1 ? "s" : ""} prioritaire${count > 1 ? "s" : ""} en attente — client premium`,
      () => navigate("/admin/tickets")
    );
  };

  const notifyFraud = (count) => {
    showNotification(
      "🛡️ IMPERIUM · Alerte anti-fraude",
      `${count} compte${count > 1 ? "s" : ""} suspect${count > 1 ? "s" : ""} (multi-comptes) à vérifier`,
      () => navigate("/admin/users")
    );
  };

  const notifyDefect = (count) => {
    showNotification(
      "⚠️ IMPERIUM · Problème signalé",
      `${count} fichier${count > 1 ? "s" : ""} livré${count > 1 ? "s" : ""} avec un problème signalé par un client`,
      () => navigate("/admin/orders")
    );
  };

  // Chime + browser notification when a new message arrives.
  useEffect(() => {
    unreadRef.current = unread;
    if (unread > prevUnread.current) { playNotify("message"); notifyNew(unread); }
    prevUnread.current = unread;
  }, [unread]);

  // Urgent alert + browser notification when a new unhandled order arrives (admin).
  useEffect(() => {
    newOrdersRef.current = newOrders;
    if (mode === "admin" && newOrders > prevNewOrders.current) { playNotify("order"); notifyNewOrder(newOrders); }
    prevNewOrders.current = newOrders;
  }, [newOrders, mode]);

  // Pleasant "file ready" chime + notification when the workshop delivers a file (client).
  useEffect(() => {
    if (mode !== "client" || readyFiles == null) return;
    let seen = parseInt(sessionStorage.getItem("imp_ready_seen") ?? "", 10);
    if (Number.isNaN(seen)) { sessionStorage.setItem("imp_ready_seen", String(readyFiles)); return; }
    if (readyFiles > seen) {
      playNotify("ready");
      showNotification("IMPERIUM · Fichier prêt", "Votre fichier est prêt à télécharger 🎉", () => navigate("/client/orders"));
      toast.success("🎉 Votre fichier est prêt à télécharger !");
    }
    sessionStorage.setItem("imp_ready_seen", String(readyFiles));
  }, [readyFiles, mode]);

  // Distinct EXPRESS siren + browser notification when a priority ticket awaits (admin).
  useEffect(() => {
    expressRef.current = expressTickets;
    if (mode === "admin" && expressTickets > prevExpress.current && getNotifPrefs().enabled) { playExpressAlert(); notifyExpress(expressTickets); }
    prevExpress.current = expressTickets;
  }, [expressTickets, mode]);

  // Anti-fraud alert when a new multi-account signup is flagged (admin).
  useEffect(() => {
    if (mode === "admin" && fraudAlerts > prevFraud.current) {
      if (getNotifPrefs().enabled) playAlert();
      notifyFraud(fraudAlerts);
      toast.warning(`🛡️ ${fraudAlerts} compte(s) suspect(s) détecté(s) — vérifiez "Comptes liés"`);
    }
    prevFraud.current = fraudAlerts;
  }, [fraudAlerts, mode]);

  // Real-time alert when a client reports a problem on a delivered file (admin).
  useEffect(() => {
    if (mode === "admin" && defectReports > prevDefect.current) {
      if (getNotifPrefs().enabled) playAlert();
      notifyDefect(defectReports);
      toast.warning(`⚠️ ${defectReports} problème(s) signalé(s) sur un fichier livré — à vérifier dans la file de traitement`);
    }
    prevDefect.current = defectReports;
  }, [defectReports, mode]);

  // Flashing tab title while there are unread messages or untreated orders.
  useEffect(() => {
    const pending = unread + (mode === "admin" ? newOrders + expressTickets : 0);
    if (pending <= 0) { document.title = baseTitle.current; return; }
    const label = mode === "admin" && expressTickets > 0
      ? `🚨 (${expressTickets > 9 ? "9+" : expressTickets}) Ticket EXPRESS !`
      : newOrders > 0 && mode === "admin"
      ? `🔴 (${newOrders > 9 ? "9+" : newOrders}) Pas encore traité`
      : `(${unread > 9 ? "9+" : unread}) Nouveau message`;
    let on = true;
    document.title = label;
    const id = setInterval(() => {
      on = !on;
      document.title = on ? label : baseTitle.current;
    }, 1100);
    return () => { clearInterval(id); document.title = baseTitle.current; };
  }, [unread, newOrders, expressTickets, mode]);

  // Recurring reminder: EXPRESS tickets (urgent, 30s) and unread chat messages (30s).
  useEffect(() => {
    const id = setInterval(() => {
      if (mode === "admin" && expressRef.current > 0) {
        playExpressAlert();
        if (document.hidden) notifyExpress(expressRef.current);
      } else if (unreadRef.current > 0) {
        playNotify("message");
        if (document.hidden) notifyNew(unreadRef.current);
      }
    }, 30000);
    return () => clearInterval(id);
  }, [mode]);

  // Gentle reminder every minute: replays the user's "Nouvelle demande" sound as
  // long as an order is not taken in charge (admin). EXPRESS tickets keep priority.
  useEffect(() => {
    if (mode !== "admin") return;
    const id = setInterval(() => {
      if (expressRef.current > 0) return; // don't stack over the urgent siren
      if (newOrdersRef.current > 0 && getNotifPrefs().enabled) {
        playNotify("order");
        if (document.hidden) notifyNewOrder(newOrdersRef.current);
      }
    }, 60000);
    return () => clearInterval(id);
  }, [mode]);

  const isActive = (item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <div className="relative min-h-screen text-white">
      {mode === "client" && <OnboardingDialog />}
      <AuroraBackground variant="muted" />
      <div className="relative z-10 flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden md:flex flex-col w-64 border-r border-white/5 bg-black/40 backdrop-blur-xl">
          <Link to={mode === "admin" ? "/admin" : "/client"} className="flex items-center gap-3 px-6 h-20 border-b border-white/5">
            <BrandMark size="md" subtitle={mode === "admin" ? "Atelier · Admin" : "Automotive Files"} />
          </Link>

          <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
            {visibleNav.map((item) => {
              const Icon = item.icon;
              if (item.children) {
                const groupActive = location.pathname.startsWith("/admin/settings");
                const open = openGroup === item.label || groupActive;
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => setOpenGroup(open && openGroup === item.label ? null : item.label)}
                      data-testid="sidebar-nav-parent-parametres"
                      className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-sm font-display uppercase tracking-wide text-sm transition-all ${
                        groupActive
                          ? "bg-aurora-cyan/10 text-aurora-cyan border-l-2 border-aurora-cyan"
                          : "text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                      }`}
                    >
                      <Icon className="w-[18px] h-[18px]" />
                      {item.label}
                      <ChevronDown className={`ml-auto w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="ml-5 pl-4 border-l border-white/10 space-y-0.5 my-1">
                            {item.children.map((sub) => {
                              const subActive = location.pathname === sub.to ||
                                (sub.to === "/admin/settings/payments" && location.pathname === "/admin/settings");
                              const SubIcon = sub.icon;
                              return (
                                <Link
                                  key={sub.to}
                                  to={sub.to}
                                  data-testid={`sidebar-nav-sub-${sub.to.split("/").pop()}`}
                                  className={`group flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] tracking-wide transition-all hover:translate-x-0.5 ${
                                    subActive
                                      ? "text-white font-medium bg-white/5"
                                      : "text-zinc-500 hover:text-zinc-200"
                                  }`}
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${subActive ? "bg-aurora-cyan" : "bg-zinc-600 group-hover:bg-zinc-400"}`} />
                                  {SubIcon && <SubIcon className="w-4 h-4" />}
                                  {sub.label}
                                </Link>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              const active = isActive(item);
              const untreated = mode === "admin" && item.to === "/admin/orders" && newOrders > 0;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.split("/").pop() || "home"}`}
                  className={`group flex items-center gap-3 px-3 py-2.5 rounded-sm font-display uppercase tracking-wide text-sm transition-all hover:translate-x-0.5 ${
                    untreated
                      ? "alert-untreated border-l-2"
                      : active
                      ? "bg-aurora-cyan/10 text-aurora-cyan border-l-2 border-aurora-cyan"
                      : "text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                  }`}
                >
                  <Icon className="w-[18px] h-[18px]" />
                  {untreated ? "Pas encore traité" : navLabel(item)}
                  {untreated && (
                    <span data-testid="nav-untreated-badge" className="badge-untreated ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full font-mono text-[10px] font-bold">
                      {newOrders > 9 ? "9+" : newOrders}
                    </span>
                  )}
                  {!untreated && unread > 0 && item.to === badgeTarget && (
                    <span data-testid="nav-unread-badge" className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-signal-error text-white font-mono text-[10px] font-bold animate-pulse">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                  {mode === "admin" && (item.to === "/admin/orders" || item.to === "/admin/defects") && defectReports > 0 && (
                    <span data-testid={`nav-defect-badge-${item.to.split("/").pop()}`} title={`${defectReports} problème(s) signalé(s) par un client sur un fichier livré`} className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-signal-error text-white font-mono text-[10px] font-bold animate-pulse">
                      ⚠ {defectReports > 9 ? "9+" : defectReports}
                    </span>
                  )}
                  {mode === "admin" && item.to === "/admin/solutions" && pendingVehicles > 0 && (
                    <span data-testid="nav-pending-vehicles-badge" title={`${pendingVehicles} véhicule(s) à valider dans le catalogue`} className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-signal-processing text-black font-mono text-[10px] font-bold">
                      {pendingVehicles > 9 ? "9+" : pendingVehicles}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {mode === "client" && user?.role === "admin" && (
            <button
              onClick={() => navigate("/admin")}
              data-testid="switch-admin-btn"
              className="mx-3 mb-2 flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm font-display uppercase tracking-wide text-aurora-purple hover:bg-aurora-purple/10 transition-colors"
            >
              <ShieldCheck className="w-[18px] h-[18px]" /> Espace Admin
            </button>
          )}
          {mode === "admin" && (
            <button
              onClick={() => navigate("/client")}
              data-testid="switch-client-btn"
              className="mx-3 mb-2 flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm font-display uppercase tracking-wide text-aurora-cyan hover:bg-aurora-cyan/10 transition-colors"
            >
              <LayoutDashboard className="w-[18px] h-[18px]" /> Espace Client
            </button>
          )}

          <div className="p-3 border-t border-white/5">
            <div className="flex items-center gap-3 px-2 py-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-aurora-cyan/20 grid place-items-center text-aurora-cyan font-bold">
                  {user?.name?.[0] || "U"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate flex items-center gap-1.5"><SessionStatusDot />{user?.name}</div>
                <div className="font-mono text-[10px] text-zinc-500 truncate">{user?.email}</div>
              </div>
              <button onClick={logout} data-testid="logout-btn" className="text-zinc-500 hover:text-signal-error transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-20 border-b border-white/5 bg-black/30 backdrop-blur-xl flex items-center justify-between px-5 md:px-8 relative z-30">
            <div className="m-stripe-bar absolute top-0 left-0" />
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-menu-trigger"
                aria-label="Ouvrir le menu"
                className="md:hidden relative w-10 h-10 grid place-items-center rounded-sm border border-white/15 text-zinc-200 hover:border-aurora-cyan/50 hover:text-aurora-cyan transition-colors shrink-0"
              >
                <Menu className="w-[18px] h-[18px]" />
                {(unread > 0 || (mode === "admin" && newOrders > 0)) && (
                  <span className={`absolute -top-1 -right-1 w-4 h-4 grid place-items-center rounded-full text-white font-mono text-[9px] font-bold ${mode === "admin" && newOrders > 0 ? "badge-untreated" : "bg-signal-error"}`}>{(mode === "admin" && newOrders > 0 ? newOrders : unread) > 9 ? "9+" : (mode === "admin" && newOrders > 0 ? newOrders : unread)}</span>
                )}
              </button>
              {mode === "admin" ? (
              <nav data-testid="admin-breadcrumb" className="flex items-center gap-2 font-mono text-[11px] tracking-widest uppercase min-w-0">
                <span className="text-aurora-cyan">Admin</span>
                {location.pathname.startsWith("/admin/settings") && (
                  <>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-500">Paramètres</span>
                  </>
                )}
                <span className="text-zinc-700">/</span>
                <span className="text-zinc-100 truncate">{ADMIN_LABELS[location.pathname] || ADMIN_LABELS[location.pathname.replace(/\/$/, "")] || (location.pathname === "/admin/settings" ? "Paiements" : "Console")}</span>
              </nav>
            ) : (
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                {t("appnav.clientPortal")}
              </div>
            )}
            </div>
            <div className="flex items-center gap-3">
              {mode === "client" && <LanguageSwitcher />}
              <InstallAppButton />
              {canNotify() && (
                <button
                  onClick={enableNotif}
                  data-testid="notif-toggle"
                  title={notifPerm === "granted" ? "Notifications activées" : "Activer les notifications navigateur"}
                  className={`relative w-10 h-10 grid place-items-center rounded-sm border transition-colors ${notifPerm === "granted" ? "border-aurora-cyan/50 text-aurora-cyan bg-aurora-cyan/10" : "border-white/15 text-zinc-400 hover:border-aurora-cyan/50"}`}
                >
                  {notifPerm === "granted" ? <BellRing className="w-[18px] h-[18px]" /> : <Bell className="w-[18px] h-[18px]" />}
                  {(unread > 0 || (mode === "admin" && newOrders > 0)) && (
                    <span className={`absolute -top-1 -right-1 w-4 h-4 grid place-items-center rounded-full text-white font-mono text-[9px] font-bold ${mode === "admin" && newOrders > 0 ? "badge-untreated" : "bg-signal-error"}`}>{(mode === "admin" && newOrders > 0 ? newOrders : unread) > 9 ? "9+" : (mode === "admin" && newOrders > 0 ? newOrders : unread)}</span>
                  )}
                </button>
              )}
              <ThemePicker />
              {mode === "client" && (
                <Link
                  to="/client/credits"
                  data-testid="credit-balance"
                  className="flex items-center gap-2 px-3 md:px-4 py-2 glass rounded-sm hover:border-aurora-cyan/40 transition-colors"
                >
                  <Coins className="w-4 h-4 text-aurora-cyan" />
                  <span className="font-mono text-sm">
                    <span className="text-white font-bold">{user?.credits ?? 0}</span>
                    <span className="text-zinc-500 hidden sm:inline"> {t("common.credits")}</span>
                  </span>
                </Link>
              )}
            </div>
          </header>
          {mode === "admin" && expressTickets > 0 && (
            <Link to="/admin/tickets" data-testid="express-alert-banner"
              className="express-alert flex items-center gap-3 px-5 md:px-8 py-3 border-b border-amber-400/40 text-amber-100 relative z-20">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-400" />
              </span>
              <BellRing className="w-5 h-5 text-amber-300 shrink-0 animate-pulse" />
              <span className="font-display text-sm font-bold uppercase tracking-wide">
                {expressTickets} ticket{expressTickets > 1 ? "s" : ""} EXPRESS en attente — client{expressTickets > 1 ? "s" : ""} premium
              </span>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest underline decoration-amber-400/50">Prendre en charge →</span>
            </Link>
          )}
          {mode === "admin" && fraudAlerts > 0 && (
            <Link to="/admin/users" data-testid="fraud-alert-banner"
              className="flex items-center gap-3 px-5 md:px-8 py-3 border-b border-signal-error/40 bg-signal-error/10 text-red-100 relative z-20">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-signal-error opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-signal-error" />
              </span>
              <ShieldAlert className="w-5 h-5 text-signal-error shrink-0 animate-pulse" />
              <span className="font-display text-sm font-bold uppercase tracking-wide">
                {fraudAlerts} compte{fraudAlerts > 1 ? "s" : ""} suspect{fraudAlerts > 1 ? "s" : ""} (multi-comptes) à vérifier
              </span>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest underline decoration-signal-error/50">Voir les comptes liés →</span>
            </Link>
          )}
          <main className="flex-1 p-5 md:p-8">{children}</main>
        </div>
      </div>

      {/* Mobile navigation drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-nav-drawer">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileOpen(false)}
              className="absolute inset-0 bg-black/70 backdrop-blur-md"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              className="absolute left-0 top-0 h-full w-[82%] max-w-[320px] flex flex-col bg-ink-base/95 backdrop-blur-2xl border-r border-white/10 shadow-2xl"
            >
              <div className="m-stripe-bar absolute top-0 left-0" />
              <div className="flex items-center justify-between px-5 h-20 border-b border-white/10">
                <BrandMark size="md" subtitle={mode === "admin" ? "Atelier · Admin" : "Automotive Files"} />
                <button
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-menu-close"
                  aria-label="Fermer le menu"
                  className="w-9 h-9 grid place-items-center rounded-sm border border-white/15 text-zinc-400 hover:border-signal-error hover:text-signal-error transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <nav className="flex-1 py-5 px-3 space-y-1 overflow-y-auto">
                {visibleNav.map((item, idx) => {
                  const Icon = item.icon;
                  if (item.children) {
                    return (
                      <motion.div
                        key={item.label}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.08 + idx * 0.05 }}
                      >
                        <div className="flex items-center gap-3 px-3 pt-3 pb-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                          <Icon className="w-4 h-4" /> {item.label}
                        </div>
                        <div className="ml-3 pl-3 border-l border-white/10 space-y-0.5">
                          {item.children.map((sub) => {
                            const subActive = location.pathname === sub.to ||
                              (sub.to === "/admin/settings/payments" && location.pathname === "/admin/settings");
                            const SubIcon = sub.icon;
                            return (
                              <Link
                                key={sub.to}
                                to={sub.to}
                                data-testid={`mobile-nav-sub-${sub.to.split("/").pop()}`}
                                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm transition-colors ${subActive ? "text-white font-medium bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
                              >
                                {SubIcon && <SubIcon className="w-4 h-4" />}
                                {sub.label}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    );
                  }
                  const active = isActive(item);
                  const untreated = mode === "admin" && item.to === "/admin/orders" && newOrders > 0;
                  return (
                    <motion.div
                      key={item.to}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.08 + idx * 0.05 }}
                    >
                      <Link
                        to={item.to}
                        data-testid={`mobile-nav-${item.to.split("/").pop() || "home"}`}
                        className={`group flex items-center gap-3 px-3 py-3 rounded-sm font-display uppercase tracking-wide text-sm transition-all ${
                          untreated
                            ? "alert-untreated border-l-2"
                            : active
                            ? "bg-aurora-cyan/10 text-aurora-cyan border-l-2 border-aurora-cyan"
                            : "text-zinc-300 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                        }`}
                      >
                        <Icon className="w-[18px] h-[18px]" />
                        {untreated ? "Pas encore traité" : navLabel(item)}
                        {untreated && (
                          <span className="badge-untreated ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full font-mono text-[10px] font-bold">
                            {newOrders > 9 ? "9+" : newOrders}
                          </span>
                        )}
                        {!untreated && unread > 0 && item.to === badgeTarget && (
                          <span className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-signal-error text-white font-mono text-[10px] font-bold animate-pulse">
                            {unread > 9 ? "9+" : unread}
                          </span>
                        )}
                      </Link>
                    </motion.div>
                  );
                })}

                {mode === "client" && user?.role === "admin" && (
                  <button
                    onClick={() => { setMobileOpen(false); navigate("/admin"); }}
                    data-testid="mobile-switch-admin-btn"
                    className="w-full flex items-center gap-2 px-3 py-3 rounded-sm text-sm font-display uppercase tracking-wide text-aurora-purple hover:bg-aurora-purple/10 transition-colors"
                  >
                    <ShieldCheck className="w-[18px] h-[18px]" /> Espace Admin
                  </button>
                )}
                {mode === "admin" && (
                  <button
                    onClick={() => { setMobileOpen(false); navigate("/client"); }}
                    data-testid="mobile-switch-client-btn"
                    className="w-full flex items-center gap-2 px-3 py-3 rounded-sm text-sm font-display uppercase tracking-wide text-aurora-cyan hover:bg-aurora-cyan/10 transition-colors"
                  >
                    <LayoutDashboard className="w-[18px] h-[18px]" /> Espace Client
                  </button>
                )}
              </nav>

              <div className="p-3 border-t border-white/10">
                <div className="flex items-center gap-3 px-2 py-2">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-white/10" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-aurora-cyan/20 grid place-items-center text-aurora-cyan font-bold">
                      {user?.name?.[0] || "U"}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate flex items-center gap-1.5"><SessionStatusDot />{user?.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 truncate">{user?.email}</div>
                  </div>
                  <button onClick={logout} data-testid="mobile-logout-btn" className="text-zinc-500 hover:text-signal-error transition-colors">
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};
